import { signal, computed, inject } from '@angular/core';
import { sortCatalog } from '@app/shared/utils/sort.utils';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

// ─── Tipos ──────────────────────────────────────────────────────────────────

export interface WsMessage<T = any> {
  event: string;
  data: T;
}

export interface WsConnectOptions {
  module: string;
  org_id?: string;
}

const WS_MODULES_WITHOUT_ORG = ['LOGIN', 'MENU'];

// ─── Clase base ─────────────────────────────────────────────────────────────

/** Recursos que son catálogos: se devuelven ordenados alfabéticamente. */
const CATALOG_RESOURCES = new Set<string>([
  'accessories', 'affiliates', 'brands', 'clients', 'courses', 'doctors', 'equipments', 'families',
  'hospitalServices', 'inhouses', 'institutions', 'instrumentals', 'insurers', 'kits', 'manufacturers',
  'measurementInstruments', 'measurementUnits', 'medicalGasTanks', 'operationRooms', 'patients',
  'paymentMethods', 'peripheralEquipment', 'places', 'procedures', 'products', 'rfidDevices', 'roles',
  'servicepackages', 'spareparts', 'specialties', 'staff', 'stages', 'substitutes', 'suppliers',
  'supplies', 'taxRegimes', 'users', 'warehouses',
]);

export abstract class ApiBaseService<T> {
  // ── URLs desde environment (todo viene del .env vía setenv.ts) ──────────
  protected readonly baseUrl = environment.API_URL;
  /**
   * API transversal de Epione (usuarios, organizaciones, roles de plataforma).
   * Medtronic habla con DOS backends: el propio (`API_URL`) y el core
   * (`CORE_API_URL`). Ambos pasan por el mismo interceptor de auth.
   */
  protected readonly coreUrl = (environment as { CORE_API_URL?: string }).CORE_API_URL ?? '';
  private readonly ticketUrl = (environment as { WS_TICKET_URL?: string }).WS_TICKET_URL ?? '';
  private readonly wsBaseUrl = (environment as { WS_BASE_URL?: string }).WS_BASE_URL ?? '';

  // ── Tiempos según documentación ──────────────────────────────────────────
  private readonly WS_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
  private readonly WS_MAX_LIFETIME_MS = 2 * 60 * 60 * 1000;

  // ── Signals de estado HTTP ───────────────────────────────────────────────
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly listData = signal<T[]>([]);
  readonly itemData = signal<T | null>(null);

  readonly hasError = computed(() => this.error() !== null);
  readonly isEmpty = computed(() => this.listData().length === 0);

  // ── Signals de estado WebSocket ──────────────────────────────────────────
  readonly wsConnected = signal(false);
  readonly wsLastMessage = signal<WsMessage | null>(null);

  private _wsMessages$ = new Subject<WsMessage>();
  readonly wsMessages$: Observable<WsMessage> = this._wsMessages$.asObservable();

  // ── Estado interno WS ────────────────────────────────────────────────────
  private ws: WebSocket | null = null;
  private wsOptions: WsConnectOptions | null = null;
  private wsHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private wsLifetimeTimer: ReturnType<typeof setTimeout> | null = null;
  private wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private wsAttempts = 0;

  // ── HttpClient: el authInterceptor se encarga del Bearer ────────────────
  protected readonly http = inject(HttpClient);

  constructor(protected resource: string) {}

  /** Nombre del recurso en el API (p. ej. 'interventions'). */
  get resourceName(): string { return this.resource; }

  // ─── HTTP: método central ────────────────────────────────────────────────
  protected async request<R>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body?: any
  ): Promise<Utils.ApiResponse<R>> {
    return this.requestAt<R>(this.baseUrl, endpoint, method, body);
  }

  /**
   * Igual que `request`, pero contra una base distinta a `API_URL`
   * (típicamente `coreUrl`). Comparte signals de loading/error y el mismo
   * manejo de errores, para que TODO el tráfico HTTP pase por un solo camino.
   */
  protected async requestAt<R>(
    base: string,
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body?: any
  ): Promise<Utils.ApiResponse<R>> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${base}/${endpoint}`;

    // Sin headers propios: el authInterceptor adjunta `Authorization: Bearer
    // <idToken>` y el API lo valida en TODAS las rutas (ya no existe bypass).
    try {
      const result = await firstValueFrom(
        this.http.request<Utils.ApiResponse<R>>(method, url, {
          body: body ?? undefined,
        })
      );
      return result;
    } catch (err) {
      // Extraemos el mensaje REAL del backend (cuerpo de la respuesta) para
      // que la vista pueda mostrar el motivo del error en vez de un genérico.
      let backendMsg: string | null = null;
      if (err instanceof HttpErrorResponse) {
        const body: any = err.error;
        if (typeof body === 'string' && body.trim()) backendMsg = body.trim();
        else if (body && typeof body === 'object') {
          backendMsg = body.message ?? body.error ?? body.detail ?? null;
        }
      }
      const message =
        backendMsg
          ?? (err instanceof HttpErrorResponse
                ? `Error ${err.status}: ${err.statusText || err.message}`
                : (err as Error)?.message ?? 'Error desconocido');
      this.error.set(message);
      console.error(`[${this.resource}] Error en ${endpoint}:`, err);

      // Lanzamos un Error con mensaje legible, preservando `status` (para los
      // chequeos que lo usan) y el error original.
      const readable: any = new Error(message);
      readable.status   = err instanceof HttpErrorResponse ? err.status : undefined;
      readable.original = err;
      throw readable;
    } finally {
      this.loading.set(false);
    }
  }

  // ─── HTTP: CRUD ──────────────────────────────────────────────────────────

  public async search(data: any): Promise<Utils.ApiResponse<T>> {
    const result = await this.request<T>(`${this.resource}/search`, 'POST', data);
    // Catálogos: SIEMPRE en orden alfabético (alias primero si lo tienen,
    // luego nombre, código…). Las búsquedas paginadas del servidor no se tocan.
    if (CATALOG_RESOURCES.has(this.resource) && Array.isArray(result.data) && data?.searchType !== 'page') {
      (result as any).data = sortCatalog(result.data as any[]);
    }
    this.listData.set(Array.isArray(result.data) ? result.data : []);
    return result;
  }

  public async create(item: T): Promise<Utils.ApiResponse<T>> {
    const result = await this.request<T>(this.resource, 'POST', item);
    this.itemData.set(Array.isArray(result.data) ? result.data[0] ?? null : null);
    return result;
  }

  public async update(item: T): Promise<Utils.ApiResponse<T>> {
    const result = await this.request<T>(this.resource, 'PATCH', item);
    this.itemData.set(Array.isArray(result.data) ? result.data[0] ?? null : null);
    return result;
  }

  // ─── WebSocket: Paso 1 — Obtener ticket vía HttpClient ─────────────────
  // Pasa por el interceptor → adjunta el Bearer automáticamente.
  private async createWsTicket(options: WsConnectOptions): Promise<string> {
    const body: Record<string, string> = { module: options.module };

    if (!WS_MODULES_WITHOUT_ORG.includes(options.module)) {
      if (!options.org_id) throw new Error(`El módulo "${options.module}" requiere org_id`);
      body['org_id'] = options.org_id;
    }

    // El authInterceptor adjunta el Bearer para autenticar la petición.
    const response = await firstValueFrom(
      this.http.post<{ ticket: string }>(this.ticketUrl, body)
    );

    return response.ticket;
  }

  // ─── WebSocket: Paso 2 — Conectar con ticket ─────────────────────────────
  public async wsConnect(options: WsConnectOptions): Promise<void> {
    this.wsDisconnect();
    this.wsOptions = options;

    try {
      const ticket = await this.createWsTicket(options);
      const url = `${this.wsBaseUrl}?ticket=${encodeURIComponent(ticket)}`;

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.wsConnected.set(true);
        this.wsAttempts = 0;
        console.debug(`[${this.resource}] WebSocket conectado (módulo: ${options.module})`);
        this.startHeartbeat();
        this.startLifetimeTimer();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const parsed: WsMessage = JSON.parse(event.data);
          this.wsLastMessage.set(parsed);
          this._wsMessages$.next(parsed);
        } catch {
          console.warn(`[${this.resource}] Mensaje WS no es JSON:`, event.data);
        }
      };

      this.ws.onerror = (err) => {
        this.error.set('Error en WebSocket');
        console.error(`[${this.resource}] WebSocket error:`, err);
      };

      this.ws.onclose = () => {
        this.wsConnected.set(false);
        this.stopHeartbeat();
        this.stopLifetimeTimer();
        console.debug(`[${this.resource}] WebSocket cerrado. Reconectando con nuevo ticket...`);
        this.wsScheduleReconnect();
      };
    } catch (err: any) {
      this.error.set(err?.message ?? 'Error al conectar WebSocket');
      console.warn(`[${this.resource}] No se pudo abrir el WebSocket; se reintenta.`);
      this.wsScheduleReconnect();
    }
  }

  // ─── WebSocket: Heartbeat ────────────────────────────────────────────────
  private startHeartbeat(): void {
    this.wsHeartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ event: 'ping', data: {} }));
        console.debug(`[${this.resource}] Heartbeat enviado`);
      }
    }, this.WS_HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.wsHeartbeatTimer) {
      clearInterval(this.wsHeartbeatTimer);
      this.wsHeartbeatTimer = null;
    }
  }

  // ─── WebSocket: Lifetime ─────────────────────────────────────────────────
  private startLifetimeTimer(): void {
    this.wsLifetimeTimer = setTimeout(() => {
      console.log(`[${this.resource}] WS llegó a 2h de vida. Reconectando...`);
      this.wsConnect(this.wsOptions!);
    }, this.WS_MAX_LIFETIME_MS);
  }

  private stopLifetimeTimer(): void {
    if (this.wsLifetimeTimer) {
      clearTimeout(this.wsLifetimeTimer);
      this.wsLifetimeTimer = null;
    }
  }

  // ─── WebSocket: Reconexión ────────────────────────────────────────────────
  private wsScheduleReconnect(): void {
    if (!this.wsOptions) return;
    // 3 s, 6 s, 12 s… hasta 1 min entre intentos (se reinicia al conectar).
    const delay = Math.min(60_000, 3000 * 2 ** Math.min(this.wsAttempts, 5));
    this.wsAttempts++;
    const options = this.wsOptions;
    if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
    this.wsReconnectTimer = setTimeout(() => {
      if (this.wsOptions === options) void this.wsConnect(options);
    }, delay);
  }

  // ─── WebSocket: Envío de mensajes ─────────────────────────────────────────
  public wsSend<P = any>(event: string, payload: P): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`[${this.resource}] WebSocket no está abierto`);
      return;
    }
    this.ws.send(JSON.stringify({ event, data: payload }));
  }

  // ─── WebSocket: Desconectar ───────────────────────────────────────────────
  public wsDisconnect(): void {
    this.stopHeartbeat();
    this.stopLifetimeTimer();

    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.wsConnected.set(false);
    this.wsOptions = null;
  }
}